/**
 * App root.
 *
 * ==================== LANGUAGE POLICY ====================
 * The Admin app is VIETNAMESE ONLY, by product spec. There is deliberately no
 * i18n framework here — no react-i18next, no message catalogue, no `t()`. Every
 * string in this app is a Vietnamese literal written directly in the JSX.
 *
 * Multi-language exists in exactly one place in this product: the CUSTOMER QR
 * ordering surface, whose strings come from the translation API
 * (`/api/v1/customer/ui-strings` and the translated menu bundles). The Admin
 * app's job regarding languages is to REVIEW and PUBLISH those translations —
 * see the "Bản dịch" screen — not to be translated itself.
 *
 * If the chain ever hires non-Vietnamese-speaking regional managers, this is
 * the comment to come back to; the change is a real project, not a switch.
 * =========================================================
 *
 * Routing: every route is generated from `navigation.ts` and wrapped in
 * `RequirePermission`, so the menu and the router can never disagree.
 */
import { Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Button, ConfirmProvider, EmptyState, LoadingState, ToastProvider } from '@sonauto/ui';
import { queryClient } from './lib/api.js';
import { AuthProvider, useAuth } from './auth/AuthProvider.js';
import { RequirePermission } from './auth/RequirePermission.js';
import { LoginPage } from './auth/LoginPage.js';
import { AppShell } from './layout/AppShell.js';
import { NAV_ENTRIES, landingPathFor } from './navigation.js';

export function App(): React.JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <ConfirmProvider>
          <BrowserRouter>
            <AuthProvider>
              <RootRoutes />
            </AuthProvider>
          </BrowserRouter>
        </ConfirmProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}

function RootRoutes(): React.JSX.Element {
  const { status, has } = useAuth();

  if (status === 'loading') {
    return (
      <div className="flex h-full items-center justify-center bg-canvas">
        <LoadingState label="Đang khôi phục phiên đăng nhập…" />
      </div>
    );
  }

  if (status === 'anonymous') {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/* Everything else bounces to login, preserving nothing: a deep link
            into an admin screen is not worth leaking in a redirect param. */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to={landingPathFor(has)} replace />} />
      <Route element={<AppShell />}>
        {NAV_ENTRIES.map((entry) => {
          const Element = entry.element;
          return (
            <Route
              key={entry.id}
              path={entry.path}
              element={
                <RequirePermission permission={entry.permission}>
                  <Suspense fallback={<LoadingState />}>
                    <Element />
                  </Suspense>
                </RequirePermission>
              }
            />
          );
        })}
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

function NotFound(): React.JSX.Element {
  const location = useLocation();
  const { has } = useAuth();
  return (
    <EmptyState
      icon="🧭"
      title="Không tìm thấy màn hình"
      description={
        <>
          Đường dẫn <code className="font-mono">{location.pathname}</code> không tồn tại, hoặc bạn không có quyền
          truy cập nên nó không được nạp.
        </>
      }
      action={
        <Button variant="secondary" onClick={() => window.location.assign(landingPathFor(has))}>
          Về trang chủ
        </Button>
      }
    />
  );
}

export default App;
